import { compareAsc, compareDesc } from 'date-fns'
import humanizeString from 'humanize-string'
import React from 'react'

import {
  BookingPaymentBalanceStatus,
  BookingPaymentDisputeStatus,
  BookingPaymentStatus,
  CreditCardType,
  PaymentSourceViewKind,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import {
  BadgeColors,
  BadgeColorsProps,
  formatMoney,
  Margin,
  parseISODate,
  Tooltip,
} from '@avantstay/backoffice-core'
import { IconInfo } from '@avantstay/backoffice-vectors'

import { PaymentSourceView } from '@arriere/types/Payments.types'

import { BookingDisputesView, BookingPaymentView } from './_types'
import { HistoryItem } from './screens/bookingPayment/Transactions/_types'

export const colorByPaymentStatus: Record<string, BadgeColorsProps> = {
  [BookingPaymentStatus.awaiting]: BadgeColors.neutral,
  [BookingPaymentStatus.received]: BadgeColors.accent,
  [BookingPaymentStatus.collected]: BadgeColors.success,
  [BookingPaymentStatus.canceled]: BadgeColors.failure,
  [BookingPaymentStatus.failed]: BadgeColors.failure,
  [BookingPaymentStatus.disputed]: BadgeColors.warning,
  [BookingPaymentStatus.disputeWon]: BadgeColors.success,
  [BookingPaymentStatus.disputeLost]: BadgeColors.failure,
  [BookingPaymentStatus.pending]: BadgeColors.neutral,
}

const colorByDisputeStatus: Record<string, BadgeColorsProps> = {
  [BookingPaymentDisputeStatus.won]: BadgeColors.success,
  [BookingPaymentDisputeStatus.lost]: BadgeColors.failure,
  [BookingPaymentDisputeStatus.underReview]: BadgeColors.warning,
  [BookingPaymentDisputeStatus.needsResponse]: BadgeColors.warning,
  [BookingPaymentDisputeStatus.warningNeedsResponse]: BadgeColors.warning,
  [BookingPaymentDisputeStatus.warningClosed]: BadgeColors.warning,
  [BookingPaymentDisputeStatus.warningUnderReview]: BadgeColors.warning,
}

const colorByPaymentBalanceStatus: Record<string, BadgeColorsProps> = {
  [BookingPaymentBalanceStatus.notPaid]: BadgeColors.notPaid,
  [BookingPaymentBalanceStatus.partlyPaid]: BadgeColors.partiallyPaid,
  [BookingPaymentBalanceStatus.fullyPaid]: BadgeColors.fullyPaid,
  [BookingPaymentBalanceStatus.overPaid]: BadgeColors.overPaid,
}

const badgeTextByPaymentBalanceStatus: Record<string, string> = {
  [BookingPaymentBalanceStatus.notPaid]: 'Not Paid',
  [BookingPaymentBalanceStatus.partlyPaid]: 'Partially Paid',
  [BookingPaymentBalanceStatus.fullyPaid]: 'Fully Paid',
  [BookingPaymentBalanceStatus.overPaid]: 'Over Paid',
}

export const displayCreditCardTypeMapper: Record<string, string> = {
  [CreditCardType.visa]: 'Visa',
  [CreditCardType.mastercard]: 'MasterCard',
  [CreditCardType.americanExpress]: 'American Express',
  [CreditCardType.discover]: 'Discover',
  [CreditCardType.amex]: 'Amex',
  [CreditCardType.unionPay]: 'Union Pay',
  [CreditCardType.dinersClub]: 'Diners Club',
  [CreditCardType.cartesBancaires]: 'Cartes Bancaires',
  [CreditCardType.jcb]: 'JCB',
  [CreditCardType.other]: 'Credit Money',
}

export const formatPaymentSource = (paymentSource: PaymentSourceView) => {
  if (paymentSource) {
    const { kind, brand, last4, bankName } = paymentSource
    switch (kind) {
      case PaymentSourceViewKind.stripeCard:
      case PaymentSourceViewKind.card:
        return `${brand ? displayCreditCardTypeMapper[brand] : ''} **** ${last4}`
      case PaymentSourceViewKind.stripeBankAccount:
      case PaymentSourceViewKind.ach:
        return `${bankName?.bankName} **** ${bankName?.last4}`
      case PaymentSourceViewKind.affirm:
        return `Affirm`
      case PaymentSourceViewKind.stripeLink:
        return 'Link by Stripe'
      case PaymentSourceViewKind.stripeOther:
        return 'Stripe'
      case PaymentSourceViewKind.creditsRefund:
      case PaymentSourceViewKind.credits:
        return 'Travel Credits'
      default:
        return 'Other'
    }
  }

  return ''
}

export const getPaymentStatusColor = (status: BookingPaymentStatus): BadgeColorsProps => {
  return colorByPaymentStatus[status] ?? BadgeColors.neutral
}

export const getDisputeStatusColor = (status: BookingPaymentDisputeStatus): BadgeColorsProps => {
  return colorByDisputeStatus[status] ?? BadgeColors.neutral
}

export const getPaymentBalanceStatusColor = (
  status: BookingPaymentBalanceStatus,
): BadgeColorsProps => {
  return colorByPaymentBalanceStatus[status] ?? BadgeColors.neutral
}

export const getPaymentBalanceStatusBadgeText = (status: BookingPaymentBalanceStatus): string => {
  return badgeTextByPaymentBalanceStatus[status] ?? humanizeString(status)
}

/**
 * Returns the total amount that was refunded for the payment
 */
export const getPaymentTotalRefundAmount = (payment: BookingPaymentView): number => {
  return payment.refunds.reduce((totalRefund, refund) => totalRefund + refund.amount, 0)
}

export const getPaymentTotalDisputedAmount = (payment: BookingPaymentView): number => {
  return payment.disputes.reduce((totalDisputes, dispute) => totalDisputes + dispute.amount, 0)
}

export const getPaymentOriginalAndCurrentBalance = (
  payment: BookingPaymentView,
  disputeLost?: boolean,
): { original: number; current: number } => {
  const totalAmountToReduce =
    getPaymentTotalRefundAmount(payment) ||
    (disputeLost ? getPaymentTotalDisputedAmount(payment) : 0)

  return {
    original: payment.amount,
    current: payment.amount - totalAmountToReduce,
  }
}

export const isRefundable = (payment: BookingPaymentView): boolean => {
  const refundableSources = [
    PaymentSourceViewKind.card,
    PaymentSourceViewKind.affirm,
    PaymentSourceViewKind.credits,
    PaymentSourceViewKind.paymentLink,
    PaymentSourceViewKind.ach,
  ]

  if (
    !payment.source ||
    (payment.source.kind && !refundableSources.includes(payment.source.kind))
  ) {
    return false
  }

  // Need to have collected payment to be able to refund it
  const lastPaymentStatus = payment.statusTrack[payment.statusTrack.length - 1].status
  if (
    lastPaymentStatus !== BookingPaymentStatus.received &&
    lastPaymentStatus !== BookingPaymentStatus.collected
  ) {
    return false
  }

  return getPaymentTotalRefundAmount(payment) < payment.amount
}

export const getPaymentHistoryCreditsRefund = (payment: BookingPaymentView) => ({
  paymentId: payment.paymentId,
  badgeColor: BadgeColors.failure,
  badgeContent: 'Travel Credits Refund',
  time: payment.statusTrack[0]?.time,
  operator: payment.statusTrack[0]?.operator,
  amount: payment.amount,
})

export const getPaymentHistoryStatusTrack = (payment: BookingPaymentView): HistoryItem[] =>
  payment.statusTrack.map(status => ({
    paymentId: payment.paymentId,
    statusTrack: status.status,
    badgeColor: getPaymentStatusColor(status.status),
    description: status.operator?.name,
    badgeContent: humanizeString(status.status),
    time: status.time,
    operator: status.operator,
    amount: payment.amount,
  }))

export const getPaymentHistoryRefunds = (
  payment: BookingPaymentView,
  hasSimpleRefundLabel = false,
): HistoryItem[] =>
  payment.refunds.map(refund => ({
    paymentId: payment.paymentId,
    refundId: refund.id,
    badgeColor: BadgeColors.failure,
    description: `${refund.description} • ${refund.operator?.name}`,
    badgeContent: hasSimpleRefundLabel ? (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        Refunded
        <Margin left={4}>
          <Tooltip placement="top" content={refund.description}>
            <IconInfo />
          </Tooltip>
        </Margin>
      </div>
    ) : (
      <>Refunded &bull; {formatMoney(refund.amount / 100)}</>
    ),
    time: refund.time,
    operator: refund.operator,
    amount: refund.amount,
  }))

export const getPaymentHistoryDisputes = (payment: BookingPaymentView): HistoryItem[] => {
  const { disputes } = payment

  const mappedHistoryItems: HistoryItem[] = []
  for (let index = 0; index < disputes.length; index++) {
    const dispute = disputes[index]
    const { statuses } = dispute

    for (let index = 0; index < statuses.length; index++) {
      const currentStatus = statuses[index]
      const currentStatusItem = {
        paymentId: payment.paymentId,
        disputeId: dispute.id,
        badgeColor: getDisputeStatusColor(currentStatus.status),
        description: dispute.operator?.name,
        badgeContent: `${humanizeString(currentStatus.status)}`,
        time: currentStatus.time,
        operator: dispute.operator,
        amount: dispute.amount,
      }
      mappedHistoryItems.push(currentStatusItem)
    }
  }
  return mappedHistoryItems
}

export const getPaymentHistoryItems = (
  payment: BookingPaymentView,
  isAscending = true,
  hasRefundedReason = false,
): HistoryItem[] => {
  if (payment?.source?.kind === PaymentSourceViewKind.creditsRefund) {
    return [getPaymentHistoryCreditsRefund(payment)]
  }

  if (payment?.disputes?.length > 0) {
    return [...getPaymentHistoryStatusTrack(payment), ...getPaymentHistoryDisputes(payment)].sort(
      (a, b) =>
        isAscending
          ? compareAsc(parseISODate(a.time), parseISODate(b.time))
          : compareDesc(parseISODate(a.time), parseISODate(b.time)),
    )
  }

  return [
    ...getPaymentHistoryStatusTrack(payment),
    ...getPaymentHistoryRefunds(payment, hasRefundedReason),
  ].sort((a, b) =>
    isAscending
      ? compareAsc(parseISODate(a.time), parseISODate(b.time))
      : compareDesc(parseISODate(a.time), parseISODate(b.time)),
  )
}

export const getTotalRefundsBadge = (payment: BookingPaymentView): JSX.Element => {
  const totalAmount = payment.refunds
    ?.map(item => item.amount)
    .reduce((amountA, amountB) => amountA + amountB, 0)
  return <>Refunded &bull; {formatMoney(totalAmount / 100)}</>
}

export const getTotalDisputesBadge = (
  payment: BookingPaymentView,
  historyItem: HistoryItem,
): JSX.Element => {
  const totalAmount = payment.disputes
    ?.map(item => item.amount)
    .reduce((amountA, amountB) => amountA + amountB, 0)

  const badgeText =
    historyItem.badgeContent === 'Disputed'
      ? historyItem.badgeContent
      : ` Dispute ${historyItem.badgeContent}`
  return (
    <>
      {badgeText} &bull; {formatMoney(totalAmount / 100)}
    </>
  )
}

export const sortPaymentsByRecentActivity = (
  payments: BookingPaymentView[],
): BookingPaymentView[] => {
  return [...payments].sort((a, b) => {
    const aItems = getPaymentHistoryItems(a)
    const bItems = getPaymentHistoryItems(b)

    return compareDesc(
      parseISODate(aItems[aItems.length - 1].time),
      parseISODate(bItems[bItems.length - 1].time),
    )
  })
}

export const isStripePayment = (paymentId: string) =>
  paymentId.startsWith('pi_') || paymentId.startsWith('py_') || paymentId.startsWith('ch_')

export const getLastPaymentStatus = (payment: BookingPaymentView) => {
  const lastStatusTrack = payment.statusTrack.reduce(
    (lastStatus, currentStatus) =>
      compareDesc(parseISODate(lastStatus.time), parseISODate(currentStatus.time))
        ? currentStatus
        : lastStatus,
    payment.statusTrack[0],
  )

  return lastStatusTrack.status
}

export const getLastDisputeStatus = (dispute: BookingDisputesView) => {
  const lastStatusTrack = dispute.statuses.reduce(
    (lastStatus, currentStatus) =>
      compareDesc(parseISODate(lastStatus.time), parseISODate(currentStatus.time))
        ? currentStatus
        : lastStatus,
    dispute.statuses[0],
  )

  return lastStatusTrack.status
}
